/**
 * The one rule about what a payout table may total: allow under, block over.
 *
 * Paying out less than the pot holds is a legitimate choice — money held back
 * for next season, a rounding buffer, a cut to a cause — so it is reported,
 * not refused. Promising more than exists is refused, because it is a number
 * that fails the moment someone expects to be paid.
 *
 * The pot moves: it is the player count times the buy-in, so it changes every
 * time someone joins or is removed. Payouts that matched exactly yesterday are
 * over or under today through no act of the admin's, which is why an
 * exact-match rule was the wrong one.
 *
 * The real gate is the server (core/payouts.py) — this is the same rule said
 * early, so an admin sees it while typing rather than on submit.
 */

// Amounts are floats, so compare with a cent of slack: three ways of
// splitting $100 can land a hair over.
export const CENT = 0.01;

const money = (n) =>
  `$${Number(n).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

/** Why this payout table cannot be saved, or "" if it can. */
export function payoutTotalError(amounts, pot) {
  if (pot == null) return "";
  const total = amounts.reduce((sum, a) => sum + (Number(a) || 0), 0);
  const over = total - Number(pot);
  if (over > CENT) {
    return (
      `Payouts total ${money(total)} but the pot is only ${money(pot)}. ` +
      `Reduce them by ${money(over)}, or add the players you are counting on.`
    );
  }
  return "";
}

/**
 * How a payout table stands against the pot, for the running total line.
 * `state` is "exact" | "over" | "under" | "unknown" (no buy-in set).
 */
export function payoutTotals(amounts, pot) {
  const total = amounts.reduce((sum, a) => sum + (Number(a) || 0), 0);
  if (pot == null) return { total, diff: null, state: "unknown" };
  const diff = total - Number(pot);
  if (Math.abs(diff) < CENT) return { total, diff, state: "exact" };
  return { total, diff, state: diff > 0 ? "over" : "under" };
}
